import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import Layout from 'Component/Layouts/layout';
import useApiCaller from 'hooks/apiCaller';
import CreateDashboardForm from './CreateDashboardForm';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import useCredentials from 'hooks/useCredentials';
import DeleteIcon from '@mui/icons-material/Delete';

const SFLDashboards = () => {
  const [dashboards, setDashboards] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [dashboardToDelete, setDashboardToDelete] = useState(null);
  const apiCaller = useApiCaller();
  const { userData: user } = useCredentials();
  const isSuperUser = user?.learningRoles?.includes('admin');

  useEffect(() => {
    fetchDashboards();
  }, []);

  const fetchDashboards = async () => {
    try {
      const response = await apiCaller.get('/user/get-all-dashboards');
      if (response?.data?.data?.dashboards) {
        setDashboards(response.data.data.dashboards);
      }
    } catch (error) {
      console.error('Error fetching dashboards:', error);
      toast.error('Failed to fetch dashboards');
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleDashboardCreated = () => {
    fetchDashboards();
    setTabValue(0);
  };

  const handleDeleteClick = (dashboard) => {
    setDashboardToDelete(dashboard);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await apiCaller.delete(`/super-user/delete-dashboard/${dashboardToDelete._id}`);
      toast.success('Dashboard deleted successfully');
      fetchDashboards();
    } catch (error) {
      console.error('Error deleting dashboard:', error);
      toast.error('Failed to delete dashboard');
    }
    setDeleteDialogOpen(false);
    setDashboardToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDashboardToDelete(null);
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Box
          sx={{
            fontSize: '24px',
            fontWeight: 500,
            background: 'linear-gradient(to bottom, #E9FBE0 0%, #E6F0E8 100%)',
            position: 'relative',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '20px',
          }}
        >
          SFL Dashboards
        </Box>

        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{
            mb: 3,
            mt: 2,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
            },
          }}
        >
          <Tab label="View Dashboards" />
          {isSuperUser && <Tab label="Upload New Dashboard" />}
        </Tabs>

        {tabValue === 0 ? (
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              borderRadius: '8px',
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Dashboard Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Upload Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dashboards.map((dashboard) => (
                  <TableRow
                    key={dashboard._id}
                    sx={{
                      '&:hover': {
                        backgroundColor: '#f9f9f9',
                      },
                    }}
                  >
                    <TableCell>{dashboard.dashboardName}</TableCell>
                    <TableCell>{new Date(dashboard.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          href={dashboard.dashboardFileUrl}
                          target="_blank"
                          sx={{
                            borderColor: '#1976d2',
                            color: '#1976d2',
                            '&:hover': {
                              borderColor: '#1565c0',
                              backgroundColor: 'rgba(25, 118, 210, 0.04)',
                            },
                          }}
                        >
                          View
                        </Button>
                        {isSuperUser && (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleDeleteClick(dashboard)}
                            sx={{
                              borderColor: '#d32f2f',
                              color: '#d32f2f',
                              '&:hover': {
                                borderColor: '#b71c1c',
                                backgroundColor: 'rgba(211, 47, 47, 0.04)',
                              },
                            }}
                          >
                            <DeleteIcon fontSize="small" sx={{ mr: 0.5 }} />
                            Delete
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <CreateDashboardForm onDashboardCreated={handleDashboardCreated} />
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          aria-labelledby="delete-dialog-title"
        >
          <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the dashboard "{dashboardToDelete?.dashboardName}"?
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel} color="primary">
              Cancel
            </Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default SFLDashboards;
